import {
  Briefcase,
  CalendarDays,
  CalendarRange,
  ChartGantt,
  ChevronLeft,
  ChevronRight,
  Folder,
  Megaphone,
  UserCircle,
  Users,
} from 'lucide-react';
import { Select, type SelectOption } from '../ui/Select';
import { MobileFilterSheet, type FilterDescriptor } from '../ui/MobileFilterSheet';
import { useI18n } from '../../contexts/I18nContext';

export type CalendarView = 'calendar' | 'week' | 'posts' | 'gantt-projects' | 'gantt-team';

interface CalendarToolbarProps {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  /** Permission-gated tabs (projects.canView / people.canViewCapacity). */
  showGanttProjects: boolean;
  showGanttTeam: boolean;
  /** Calendar de postări tab (socialPosts.canView). */
  showPosts?: boolean;
  /** Current period label ("iulie 2026" or the Gantt horizon range). */
  label: string;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
  /** Task filters — rendered only on the calendar view. */
  showFilters: boolean;
  clientOptions?: SelectOption[];
  clientId?: string;
  onClientChange?: (id: string) => void;
  projectOptions: SelectOption[];
  projectId: string;
  onProjectChange: (id: string) => void;
  assigneeOptions: SelectOption[];
  assigneeId: string;
  onAssigneeChange: (id: string) => void;
}

/**
 * Toolbar for the operational calendar: month/horizon navigation, the
 * Calendar | Gantt projects | Gantt team segmented switcher (same pattern as
 * the list/board toggle in TasksView) and the project/assignee task filters.
 */
export function CalendarToolbar({
  view,
  onViewChange,
  showGanttProjects,
  showGanttTeam,
  showPosts = false,
  label,
  onPrev,
  onToday,
  onNext,
  showFilters,
  clientOptions = [],
  clientId = '',
  onClientChange,
  projectOptions,
  projectId,
  onProjectChange,
  assigneeOptions,
  assigneeId,
  onAssigneeChange,
}: CalendarToolbarProps) {
  const { t } = useI18n();

  // Mobile bottom-sheet filters (Linear-style) — same options/setters as the
  // desktop Selects below; the sheet only renders under `sm`.
  const mobileFilters: FilterDescriptor[] = [
    ...(onClientChange
      ? [
          {
            kind: 'select',
            id: 'client',
            label: t('calendar.filterClient'),
            icon: <Briefcase size={16} />,
            value: clientId,
            onChange: onClientChange,
            options: clientOptions,
            placeholder: t('calendar.allClients'),
          } satisfies FilterDescriptor,
        ]
      : []),
    {
      kind: 'select',
      id: 'project',
      label: t('calendar.filterProject'),
      icon: <Folder size={16} />,
      value: projectId,
      onChange: onProjectChange,
      options: projectOptions,
      placeholder: t('calendar.allProjects'),
    },
    {
      kind: 'select',
      id: 'assignee',
      label: t('calendar.filterAssignee'),
      icon: <UserCircle size={16} />,
      value: assigneeId,
      onChange: onAssigneeChange,
      options: assigneeOptions,
      placeholder: t('calendar.allAssignees'),
    },
  ];

  // Below `sm` the four labels ("Lună", "Săptămână", "Gantt proiecte", "Gantt
  // echipă") are far wider than a phone, so only the icons show; the label
  // stays as the accessible name and the tooltip.
  const segment = (target: CalendarView, text: string, icon: React.ReactNode) => (
    <button
      type="button"
      onClick={() => onViewChange(target)}
      aria-pressed={view === target}
      aria-label={text}
      title={text}
      className={`inline-flex h-[42px] flex-1 shrink-0 items-center justify-center gap-1 rounded px-2.5 text-sm sm:h-auto sm:flex-none sm:py-1.5 ${
        view === target ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon} <span className="hidden sm:inline">{text}</span>
    </button>
  );

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      {/* Phones: the month on the left, a compact ‹ Azi › on the right, one
          row. The group opts out of the 44px-per-button touch rule
          (touch-target-auto) and sizes itself to 44px instead, the height of
          the Filtre button below; otherwise the paddings stacked to ~60px. */}
      <div className="flex items-center justify-between gap-2 sm:justify-start">
        <div className="touch-target-auto order-2 inline-flex items-center rounded border border-gray-300 sm:order-1 sm:p-0.5">
          <button
            type="button"
            onClick={onPrev}
            aria-label={t('calendar.prev')}
            className="inline-flex h-[42px] w-10 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 sm:h-8 sm:w-8"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={onToday}
            className="inline-flex h-[42px] items-center rounded px-2.5 text-sm text-gray-700 hover:bg-gray-100 sm:h-8"
          >
            {t('calendar.today')}
          </button>
          <button
            type="button"
            onClick={onNext}
            aria-label={t('calendar.next')}
            className="inline-flex h-[42px] w-10 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 sm:h-8 sm:w-8"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <span className="order-1 min-w-0 truncate text-base font-semibold capitalize text-gray-900 sm:order-2 sm:min-w-40 sm:text-sm">
          {label}
        </span>
      </div>

      {/* On phones the Filtre button and the view switcher share one row (the
          switcher scroll-overflows beside it); >= sm the selects join in. */}
      <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-wrap sm:items-end">
        {showFilters && (
          <>
            {/* Desktop inline selects (>= sm only). */}
            <div className="hidden items-end gap-2 sm:flex">
              {onClientChange && (
                <Select
                  label={t('calendar.filterClient')}
                  value={clientId}
                  onChange={onClientChange}
                  options={clientOptions}
                  placeholder={t('calendar.allClients')}
                  clearable
                  searchable
                  width={220}
                  className="w-40"
                />
              )}
              <Select
                label={t('calendar.filterProject')}
                value={projectId}
                onChange={onProjectChange}
                options={projectOptions}
                placeholder={t('calendar.allProjects')}
                clearable
                searchable
                width={220}
                className="w-44"
              />
              <Select
                label={t('calendar.filterAssignee')}
                value={assigneeId}
                onChange={onAssigneeChange}
                options={assigneeOptions}
                placeholder={t('calendar.allAssignees')}
                clearable
                searchable
                width={220}
                className="w-44"
              />
            </div>
            {/* Mobile: one Filters button opening the bottom sheet. */}
            <MobileFilterSheet
              className="sm:hidden"
              filters={mobileFilters}
              onClearAll={() => {
                onClientChange?.('');
                onProjectChange('');
                onAssigneeChange('');
              }}
            />
          </>
        )}
        {/* The switcher scrolls rather than clipping: with a long locale its
            four segments can still outgrow a narrow column. */}
        <div className="-mx-0.5 min-w-0 flex-1 overflow-x-auto px-0.5 sm:mx-0 sm:flex-none sm:overflow-visible sm:px-0">
          {/* Phones: fills the row next to Filtre, icons spread evenly, 44px
              tall like it (same touch-target-auto trick as the nav above). */}
          <div className="touch-target-auto flex w-full rounded border border-gray-300 sm:inline-flex sm:w-auto sm:self-end sm:p-0.5">
            {segment('calendar', t('calendar.viewCalendar'), <CalendarDays size={15} />)}
            {segment('week', t('calendar.viewWeek'), <CalendarRange size={15} />)}
            {showPosts && segment('posts', t('calendar.viewPosts'), <Megaphone size={15} />)}
            {showGanttProjects &&
              segment('gantt-projects', t('calendar.viewGanttProjects'), <ChartGantt size={15} />)}
            {showGanttTeam &&
              segment('gantt-team', t('calendar.viewGanttTeam'), <Users size={15} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
